import * as z from "zod";
import { isoDate } from "./common";

export const STREAM_STATUSES = ["SCHEDULED", "LIVE", "ENDED", "CANCELLED"] as const;
export const BREAK_CATEGORIES = ["NBA", "MLB", "NFL", "NHL", "TCG", "CUSTOM"] as const;
export type BreakCategory = (typeof BREAK_CATEGORIES)[number];
export const BREAK_STATUSES = [
  "DRAFT",
  "ON_SALE",
  "LOCKED",
  "RANDOMIZED",
  "BREAKING",
  "COMPLETED",
  "CANCELLED",
] as const;
export const SLOT_STATUSES = ["AVAILABLE", "HELD", "SOLD", "HOUSE"] as const;
export const ORDER_STATUSES = ["PENDING", "PAID", "EXPIRED", "FAILED", "REFUNDED", "CANCELLED"] as const;

export const SEGMENT_KINDS = ["BREAK", "UNBOXING", "GUEST", "AUTOGRAPHS", "CUSTOM"] as const;
export type SegmentKind = (typeof SEGMENT_KINDS)[number];
export const SEGMENT_STATUSES = ["PLANNED", "CURRENT", "DONE"] as const;

/** One item of a stream's line-up. CURRENT is on screen; at most one is. */
export const LiveStreamSegment = z.object({
  id: z.string(),
  kind: z.enum(SEGMENT_KINDS),
  status: z.enum(SEGMENT_STATUSES),
  title: z.string(),
  position: z.number(),
  /** The box a BREAK segment rips; null for every other kind. */
  breakId: z.string().nullable(),
  /** The box's cover, for a BREAK segment. */
  imageUrl: z.string().nullable().optional(),
  /** Announcement only, for kinds other than BREAK. */
  scheduledFor: isoDate.nullable(),
  startedAt: isoDate.nullable(),
  endedAt: isoDate.nullable(),
});
export type LiveStreamSegment = z.output<typeof LiveStreamSegment>;

export const LiveStream = z.object({
  id: z.string(),
  storeId: z.string(),
  /** The shop location broadcasting. One open stream per location. */
  locationId: z.string(),
  title: z.string(),
  status: z.enum(STREAM_STATUSES),
  scheduledFor: isoDate.nullable(),
  startedAt: isoDate.nullable(),
  endedAt: isoDate.nullable(),
  video: z.object({ kind: z.literal("livekit"), roomName: z.string() }).nullable(),
  recordingReady: z.boolean(),
  coverUrl: z.string().nullable(),
  host: z.object({ id: z.string(), name: z.string(), imageUrl: z.string().nullable() }).nullable(),
  store: z.object({
    id: z.string(),
    name: z.string().nullable(),
    slug: z.string().nullable(),
    logoUrl: z.string().nullable(),
  }),
  streamPaused: z.boolean(),
  /** The line-up, in order. */
  segments: z.array(LiveStreamSegment),
  currentSegmentId: z.string().nullable(),
});
export type LiveStream = z.output<typeof LiveStream>;

export const LiveBreak = z.object({
  id: z.string(),
  /** The shop whose queue this break is in. */
  storeId: z.string(),
  /** Its place in a stream's line-up. Null while it sells from the queue in no line-up. */
  segment: z.object({ id: z.string(), streamId: z.string(), status: z.enum(SEGMENT_STATUSES) }).nullable(),
  /** Not broken earlier than this. Null means any time. */
  breakNotBefore: isoDate.nullable(),
  imageUrl: z.string().nullable().optional(),
  title: z.string(),
  category: z.enum(BREAK_CATEGORIES),
  status: z.enum(BREAK_STATUSES),
  slotCount: z.number(),
  soldCount: z.number(),
  defaultPriceCents: z.number(),
  labelPool: z.array(z.string()),
  randomizedAt: isoDate.nullable(),
  randomizerSeed: z.string().nullable(),
  randomizerAlgorithm: z.string().nullable(),
  seedCommitment: z.string().nullable(),
  seedCommittedAt: isoDate.nullable(),
});
export type LiveBreak = z.output<typeof LiveBreak>;

/** A break in the public queue, with the shop it belongs to. */
export const LiveQueueEntry = LiveBreak.extend({
  store: z.object({
    id: z.string(),
    name: z.string().nullable(),
    slug: z.string().nullable(),
    logoUrl: z.string().nullable(),
  }),
});
export type LiveQueueEntry = z.output<typeof LiveQueueEntry>;

export const LiveFinishedBreak = LiveQueueEntry.extend({ finishedAt: isoDate });
export type LiveFinishedBreak = z.output<typeof LiveFinishedBreak>;

export const LiveSlot = z.object({
  id: z.string(),
  slotNumber: z.number(),
  priceCents: z.number(),
  status: z.enum(SLOT_STATUSES),
  buyerName: z.string().nullable(),
  /** Held or bought by the caller. */
  mine: z.boolean(),
  assignedLabel: z.string().nullable(),
  pinned: z.boolean(),
});
export type LiveSlot = z.output<typeof LiveSlot>;

/** One break as a link to it previews: no spots and nobody's name, so it needs no session. */
export const LiveBreakSummary = z.object({
  break: LiveQueueEntry,
  /** Null while the break sells from the queue with no stream. */
  stream: LiveStream.nullable(),
});
export type LiveBreakSummary = z.output<typeof LiveBreakSummary>;

/** A break with its slot grid, as a buyer sees it. */
export const LiveBreakDetail = z.object({
  break: LiveBreak,
  slots: z.array(LiveSlot),
  /** Null while the break sells from the queue with no stream. */
  stream: LiveStream.nullable(),
});
export type LiveBreakDetail = z.output<typeof LiveBreakDetail>;

/**
 * A break as its host sees it: the same row plus the economics, which are
 * private. `costCents` and `marketValueCents` are what the host paid and what
 * they think it is worth — never on a buyer's copy.
 */
export const LiveBreakHost = LiveBreak.extend({
  costCents: z.number(),
  marketValueCents: z.number(),
  /** 150 = 1.5x, 200 = 2x. */
  targetMultiplierPct: z.number(),
  grossSoldCents: z.number(),
  profitIfSoldOutCents: z.number(),
  recordingStartOffsetSeconds: z.number().nullable().optional(),
  recordingEndOffsetSeconds: z.number().nullable().optional(),
  /** Boxes opened in this break. */
  boxCount: z.number(),
  /** The inventory product being opened, when it came from stock. */
  sealedProductId: z.string().nullable(),
  sealedProductName: z.string().nullable(),
  /** The last manual promote push; the next one is a day after it. */
  promotedAt: isoDate.nullable().optional(),
});
export type LiveBreakHost = z.output<typeof LiveBreakHost>;

/** A slot plus who bought it and the host's own note. Host-only. */
export const LiveSlotHost = LiveSlot.extend({
  buyerUserId: z.string().nullable(),
  buyerEmail: z.string().nullable(),
  orderId: z.string().nullable(),
  /** Carried to CSV exports and packing slips. Never shown to buyers. */
  sellerNote: z.string().nullable(),
  holdExpiresAt: isoDate.nullable(),
  /** Set once the host marked this spot as pulled on stream. */
  openedAt: isoDate.nullable().optional(),
});
export type LiveSlotHost = z.output<typeof LiveSlotHost>;

/** The host's view of one break: the economics and every buyer. */
export const LiveBreakHostDetail = z.object({
  break: LiveBreakHost,
  slots: z.array(LiveSlotHost),
});
export type LiveBreakHostDetail = z.output<typeof LiveBreakHostDetail>;

/** The host's view of a whole stream: the breaks of its line-up, each with their economics. */
export const LiveStreamHostDetail = z.object({
  stream: LiveStream,
  breaks: z.array(LiveBreakHost),
  /** The caller hosts it: draw, finish and end are theirs. */
  isHost: z.boolean(),
});
export type LiveStreamHostDetail = z.output<typeof LiveStreamHostDetail>;

/** Who is in the room, for the host: signed-in viewers, buyers first. */
export const LiveStreamViewers = z.object({
  /** False when the realtime provider could not say; `viewers` is then empty. */
  available: z.boolean(),
  viewers: z.array(
    z.object({
      userId: z.string(),
      name: z.string(),
      /** Paid spots across this stream's line-up. */
      spots: z.number(),
      /** Those spots, in line-up then spot order. */
      slots: z.array(
        z.object({
          breakId: z.string(),
          breakTitle: z.string(),
          slotNumber: z.number(),
          /** The team the draw gave it; null until the draw. */
          label: z.string().nullable(),
        }),
      ),
    }),
  ),
});
export type LiveStreamViewers = z.output<typeof LiveStreamViewers>;

export const LiveOrder = z.object({
  id: z.string(),
  breakId: z.string(),
  status: z.enum(ORDER_STATUSES),
  subtotalCents: z.number(),
  slotCount: z.number(),
  currency: z.string(),
  holdExpiresAt: isoDate.nullable(),
  paidAt: isoDate.nullable(),
  receiptReady: z.boolean(),
  slotNumbers: z.array(z.number()),
  streamId: z.string().nullable(),
  streamTitle: z.string().nullable(),
  breakTitle: z.string().nullable(),
  storeName: z.string().nullable(),
  breakImageUrl: z.string().nullable(),
  breakStatus: z.string().nullable(),
  spots: z.array(
    z.object({
      slotNumber: z.number(),
      /** The team the draw gave this spot; null until the draw. */
      label: z.string().nullable(),
      /** The parcel this spot is packed in. One parcel per buyer per shop, so spots from several boxes share it. */
      shipment: z
        .object({
          id: z.string(),
          status: z.enum([
            "AWAITING_ADDRESS",
            "AWAITING_PAYMENT",
            "PAID",
            "LABEL_CREATED",
            "SHIPPED",
            "DELIVERED",
            "CANCELLED",
          ]),
          carrier: z.string().nullable(),
          trackingNumber: z.string().nullable(),
          trackingUrl: z.string().nullable(),
        })
        .nullable(),
    }),
  ),
});
export type LiveOrder = z.output<typeof LiveOrder>;

export const LiveRecordingCookie = z.object({
  name: z.string(),
  value: z.string(),
});
export type LiveRecordingCookie = z.output<typeof LiveRecordingCookie>;

export const LiveRecordingPlayback = z.object({
  kind: z.literal("hls"),
  url: z.string(),
  cookies: z.array(LiveRecordingCookie).optional(),
  cookieDomain: z.string().nullable().optional(),
  cookieMaxAgeSeconds: z.number().nullable().optional(),
});
export type LiveRecordingPlayback = z.output<typeof LiveRecordingPlayback>;

export const LiveStreamRecordingPlayback = LiveRecordingPlayback.extend({
  cookies: z.array(LiveRecordingCookie).length(3),
});
export type LiveStreamRecordingPlayback = z.output<typeof LiveStreamRecordingPlayback>;

export const LiveOrderRecordingSpot = z.object({
  slotNumber: z.number(),
  status: z.enum(["PROCESSING", "READY", "FAILED"]),
  startOffsetSeconds: z.number().nullable(),
});
export type LiveOrderRecordingSpot = z.output<typeof LiveOrderRecordingSpot>;

export const LiveOrderRecording = z.object({
  status: z.enum(["PROCESSING", "READY", "FAILED"]),
  playback: LiveRecordingPlayback.nullable(),
  startOffsetSeconds: z.number().nullable(),
  windowStartOffsetSeconds: z.number().nullable(),
  windowEndOffsetSeconds: z.number().nullable(),
  durationSeconds: z.number().nullable(),
  spots: z.array(LiveOrderRecordingSpot),
});
export type LiveOrderRecording = z.output<typeof LiveOrderRecording>;

export const LiveStreamRecordingPart = z.object({
  recordingId: z.string(),
  state: z.enum(["READY", "PROCESSING", "FAILED", "UNAVAILABLE"]),
  durationSeconds: z.number().nullable(),
  timelineStartOffsetSeconds: z.number().nullable(),
  playback: LiveStreamRecordingPlayback.nullable(),
  credentialExpiresAt: z.string().datetime({ offset: true }).nullable(),
});
export type LiveStreamRecordingPart = z.output<typeof LiveStreamRecordingPart>;

export const LiveStreamRecording = z.object({
  status: z.enum(["NOT_RECORDED", "PROCESSING", "READY", "PARTIALLY_READY", "FAILED"]),
  totalDurationSeconds: z.number(),
  parts: z.array(LiveStreamRecordingPart),
});
export type LiveStreamRecording = z.output<typeof LiveStreamRecording>;

export const LiveShipmentAddress = z.object({
  name: z.string(),
  street1: z.string(),
  street2: z.string().nullable(),
  city: z.string(),
  state: z.string().nullable(),
  zip: z.string().nullable(),
  country: z.string(),
  phone: z.string().nullable(),
});
export type LiveShipmentAddress = z.output<typeof LiveShipmentAddress>;

export const LiveCheckoutSummary = z.object({
  lines: z.array(z.object({ slotNumber: z.number(), priceCents: z.number() })),
  subtotalCents: z.number(),
  /** The card fee on the spots, added to the charge. */
  buyerFeeCents: z.number(),
  shippingCents: z.number(),
  shippingCarrier: z.string().nullable(),
  shippingService: z.string().nullable(),
  totalCents: z.number(),
  currency: z.string(),
  carrierShipping: z.boolean(),
});
export type LiveCheckoutSummary = z.output<typeof LiveCheckoutSummary>;

/** A hold on slots plus how to pay for it. */
export const LiveCheckout = z.object({
  order: LiveOrder,
  /** A PaymentIntent client secret; null while an address is still owed. */
  clientSecret: z.string().nullable(),
  /** The connected account the intent lives on. */
  accountId: z.string(),
  summary: LiveCheckoutSummary,
  /** True when this order carries the postage for the buyer's next parcel, so an address comes before payment. */
  needsAddress: z.boolean(),
  addressPrefill: LiveShipmentAddress.nullable(),
  billingAddress: LiveShipmentAddress.nullable(),
});
export type LiveCheckout = z.output<typeof LiveCheckout>;

export interface NewStream {
  /** The shop location broadcasting. */
  locationId: string;
  title: string;
  scheduledFor?: Date;
  coverImageUrl?: string;
}

/** A line-up item: a queued box, or any other kind with a title. */
export type NewSegment =
  | { kind: "BREAK"; breakId: string }
  | { kind: Exclude<SegmentKind, "BREAK">; title: string; scheduledFor?: Date };

export interface ShippingAddress {
  name: string;
  street1: string;
  street2?: string | null;
  city: string;
  state?: string | null;
  zip?: string | null;
  country: string;
  phone?: string | null;
}

/**
 * One row of the platform back-office's cross-store live monitor.
 *
 * Not a `LiveStream`: this is the staff view, carrying the store and the money and
 * none of the video a merchant's own console needs. `platformFeeCents` is the
 * figure stored on each paid order, not one recomputed from the current rate, so
 * a total built from these matches what Stripe actually took.
 *
 * `status` is a bare string, matching the contract the backend declares — this
 * route does not narrow it to `STREAM_STATUSES`.
 */
export const AdminLiveBox = z.object({
  id: z.string(),
  title: z.string(),
  status: z.string(),
  imageUrl: z.string().nullable(),
  slotCount: z.number(),
  soldCount: z.number(),
  paidOrders: z.number(),
});
export type AdminLiveBox = z.output<typeof AdminLiveBox>;

export const AdminLiveStream = z.object({
  id: z.string(),
  title: z.string(),
  status: z.string(),
  /** LIVE with the host's camera actually sending. */
  cameraOn: z.boolean(),
  storeId: z.string(),
  storeName: z.string().nullable(),
  hostName: z.string().nullable(),
  breakCount: z.number(),
  slotCount: z.number(),
  soldCount: z.number(),
  grossCents: z.number(),
  platformFeeCents: z.number(),
  boxes: z.array(AdminLiveBox),
  startedAt: isoDate.nullable(),
  createdAt: isoDate,
});
export type AdminLiveStream = z.output<typeof AdminLiveStream>;

/** A box selling from a shop's queue, in no stream's line-up. */
export const AdminQueuedBox = AdminLiveBox.extend({
  storeId: z.string(),
  storeName: z.string().nullable(),
  grossCents: z.number(),
  platformFeeCents: z.number(),
  createdAt: isoDate,
});
export type AdminQueuedBox = z.output<typeof AdminQueuedBox>;

export const AdminClosedStream = z.object({
  cancelled: z.number(),
  refunded: z.number(),
  refundsFailed: z.number(),
});
export type AdminClosedStream = z.output<typeof AdminClosedStream>;

// ─── The buyer's side ────────────────────────────────────────────────────────

/** A stream as a card in the feed: the public facts plus what a spot costs. */
export const LiveStreamCard = LiveStream.extend({
  /** Spots across every break that is not a draft. */
  slotCount: z.number(),
  soldCount: z.number(),
  /**
   * The cheapest spot still available, in cents. Null when nothing is on sale —
   * a sold-out break and a finished stream both say nothing rather than quoting a
   * price that cannot be bought.
   */
  fromPriceCents: z.number().nullable(),
});
export type LiveStreamCard = z.output<typeof LiveStreamCard>;

/**
 * What is on, in three buckets.
 *
 * Grouped by the server rather than returned as one list to sort here, because
 * one list needs one `take`: ordered by status, Postgres sorts ENDED < LIVE <
 * SCHEDULED alphabetically, so a shop with thirty finished streams would fill the
 * limit with them and quietly stop listing anything actually happening.
 */
export const LiveStreamFeed = z.object({
  /** On air right now. */
  live: z.array(LiveStreamCard),
  /** Scheduled, not started. */
  upcoming: z.array(LiveStreamCard),
  /** Recently finished, newest first. */
  past: z.array(LiveStreamCard),
});
export type LiveStreamFeed = z.output<typeof LiveStreamFeed>;

/** One stream and its break queue, as a buyer sees it. DRAFT breaks are absent. */
export const LiveStreamDetail = z.object({
  stream: LiveStream,
  breaks: z.array(LiveBreak),
});
export type LiveStreamDetail = z.output<typeof LiveStreamDetail>;

// ─── The host's console ──────────────────────────────────────────────────────

/** Credentials for the host to publish video. Never stored, never a buyer's. */
export const LiveBroadcastCredentials = z.object({
  serverUrl: z.string(),
  /** Short-lived publish token. Never persisted anywhere. */
  token: z.string(),
  roomName: z.string(),
  provider: z.string(),
  /** Whether the host can publish from a browser with nothing installed. */
  browserBroadcast: z.boolean(),
});
export type LiveBroadcastCredentials = z.output<typeof LiveBroadcastCredentials>;

/** RTMP details for a host using OBS or a hardware encoder. */
export const LiveIngestCredentials = z.object({
  ingestUrl: z.string(),
  streamKey: z.string(),
});
export type LiveIngestCredentials = z.output<typeof LiveIngestCredentials>;

/**
 * A short-lived, capability-scoped credential for the realtime client.
 *
 * `token` is `unknown` on purpose: it is handed straight to the provider's SDK
 * and this client never looks inside it. **Null means realtime is not configured
 * in this environment**, which the console renders as a grid that refreshes on
 * its own rather than as an error.
 */
export const LiveRealtimeToken = z.object({
  provider: z.string(),
  token: z.unknown().nullable(),
});
export type LiveRealtimeToken = z.output<typeof LiveRealtimeToken>;

/** A per-viewer token admitting one person to one room, to watch only. */
export const LiveVideoToken = z.object({
  serverUrl: z.string(),
  token: z.string(),
  roomName: z.string(),
});
export type LiveVideoToken = z.output<typeof LiveVideoToken>;

export const STAGE_STATUSES = ["NONE", "HAND_RAISED", "INVITED", "ON_STAGE"] as const;
export type StageStatus = (typeof STAGE_STATUSES)[number];

export const LiveStageMember = z.object({
  userId: z.string(),
  name: z.string(),
  /** Asked to come on, and has not answered yet. */
  invited: z.boolean(),
  /** Holds a paid spot on this stream; the host's queue lists these first. */
  hasSpot: z.boolean(),
});
export type LiveStageMember = z.output<typeof LiveStageMember>;

/** Buyers on the stream, voice only: who has a hand up, who is on, and where the caller stands. */
export const LiveStage = z.object({
  raised: z.array(LiveStageMember),
  onStage: z.array(LiveStageMember),
  mine: z.enum(STAGE_STATUSES),
  /** The caller is signed in as somebody, not a guest, so may raise a hand. */
  canRaiseHand: z.boolean(),
});
export type LiveStage = z.output<typeof LiveStage>;

/** The result of asking the provider whether video is really arriving. */
export const LiveSyncStream = z.object({
  live: z.boolean(),
  /** This call is what moved the stream out of SCHEDULED. */
  transitioned: z.boolean(),
  /** Why it is not live, in words a host can act on. */
  reason: z.string().nullable(),
});
export type LiveSyncStream = z.output<typeof LiveSyncStream>;

/** The result of asking the provider whether a recording has appeared. */
export const LiveSyncRecording = z.object({
  found: z.boolean(),
  ordersReady: z.number(),
  reason: z.string().nullable(),
});
export type LiveSyncRecording = z.output<typeof LiveSyncRecording>;

export const LiveChatMessage = z.object({
  id: z.string(),
  userId: z.string(),
  authorName: z.string(),
  /** The author's Firebase UID, shared with the HobbyScan app. Null for an account without one. */
  authorUid: z.string().nullable().default(null),
  text: z.string(),
  /** USER is somebody talking; the rest are the room's own lines, under the user who did it. */
  kind: z.enum(["USER", "PURCHASE", "RIP_STARTED", "DRAW", "RIP_FINISHED"]).default("USER"),
  createdAt: isoDate,
  /** Written by the caller. */
  mine: z.boolean(),
});
export type LiveChatMessage = z.output<typeof LiveChatMessage>;

export const LiveRevealAssignment = z.object({
  slotNumber: z.number(),
  label: z.string(),
  status: z.enum(["SOLD", "HOUSE"]),
  buyerName: z.string().nullable(),
});
export type LiveRevealAssignment = z.output<typeof LiveRevealAssignment>;

/**
 * What the draw produced.
 *
 * `seed` and `algorithm` come back with it so the draw can be re-derived from
 * the commitment published beforehand — that is the whole claim the product
 * rests on, and why nothing here may be run twice.
 */
export const LiveReveal = z.object({
  breakId: z.string(),
  streamId: z.string(),
  randomizedAt: isoDate,
  seed: z.string(),
  algorithm: z.string(),
  soldCount: z.number(),
  houseCount: z.number(),
  assignments: z.array(LiveRevealAssignment),
});
export type LiveReveal = z.output<typeof LiveReveal>;

/** One row of a pasted spot list, as the server read it. */
export const LiveParsedSpot = z.object({
  label: z.string(),
  /** Null means this spot takes the break's default price. */
  priceCents: z.number().nullable(),
  /** Host-only. Carried to CSV exports and packing slips. */
  note: z.string().nullable(),
});
export type LiveParsedSpot = z.output<typeof LiveParsedSpot>;

/**
 * What the server made of a pasted spot list.
 *
 * The warnings and errors are the reason this is a round trip rather than
 * client-side parsing: a host pasting three hundred rows needs to be told that
 * line 41 had no name and line 88 repeats line 12 before the stream, not during.
 */
export const LiveParsedSpots = z.object({
  spots: z.array(LiveParsedSpot),
  /** The row was used, but something was ignored or assumed. */
  warnings: z.array(z.string()),
  /** Fatal for that row: it was skipped. */
  errors: z.array(z.string()),
  delimiter: z.string(),
  headerSkipped: z.boolean(),
});
export type LiveParsedSpots = z.output<typeof LiveParsedSpots>;

/** A ready-made label pool — the teams in a league, in a fixed order. */
export const LiveSpotTemplate = z.object({
  key: z.string(),
  name: z.string(),
  category: z.string(),
  labels: z.array(z.string()),
});
export type LiveSpotTemplate = z.output<typeof LiveSpotTemplate>;

/** A spot list with where it came from. Built-in lists have no id; saved ones use their id as the key. */
export const LiveSpotList = LiveSpotTemplate.extend({
  id: z.string().nullable(),
  source: z.enum(["system", "shop"]),
});
export type LiveSpotList = z.output<typeof LiveSpotList>;

export interface NewSpotList {
  name: string;
  category: BreakCategory;
  labels: string[];
}

/** A promote push that went out. */
export const LivePromotedBreak = z.object({
  /** Devices it reached. */
  sent: z.number(),
  promotedAt: isoDate,
});
export type LivePromotedBreak = z.output<typeof LivePromotedBreak>;

/** What cancelling a break did to its paid orders. A failed refund is money the buyer is still owed. */
export const LiveCancelledBreak = z.object({
  refunded: z.number(),
  refundsFailed: z.number(),
});
export type LiveCancelledBreak = z.output<typeof LiveCancelledBreak>;

/** What joined the line-up. */
export const LiveSegmentsAdded = z.object({
  segmentIds: z.array(z.string()),
  /** The boxes among them. */
  breakIds: z.array(z.string()),
});
export type LiveSegmentsAdded = z.output<typeof LiveSegmentsAdded>;

// ─── Shipping ────────────────────────────────────────────────────────────────

export const SHIPMENT_STATUSES = [
  "AWAITING_ADDRESS",
  "AWAITING_PAYMENT",
  "PAID",
  "LABEL_CREATED",
  "SHIPPED",
  "DELIVERED",
  "CANCELLED",
] as const;

export const LiveShippingLine = z.object({
  breakId: z.string(),
  breakTitle: z.string(),
  slotId: z.string(),
  slotNumber: z.number(),
  /** The team or spot they ended up with. */
  assignedLabel: z.string().nullable(),
  /** Host-only. */
  sellerNote: z.string().nullable(),
  pricePaidCents: z.number(),
  /** When the box was broken. */
  brokenAt: isoDate.nullable(),
});
export type LiveShippingLine = z.output<typeof LiveShippingLine>;

/** What one buyer has waiting at one location, not yet in a parcel. */
export const LiveReadyBundle = z.object({
  buyerUserId: z.string(),
  buyerName: z.string(),
  /** Host-only. */
  buyerEmail: z.string().nullable(),
  /** Where the cards are: the location the boxes were broken at. */
  locationId: z.string().nullable(),
  lines: z.array(LiveShippingLine),
  totalPaidCents: z.number(),
  /** When its oldest box was broken. */
  waitingSince: isoDate.nullable(),
  /** What checkout collected; the parcel opens with it. */
  address: LiveShipmentAddress.nullable(),
  postagePaidCents: z.number(),
});
export type LiveReadyBundle = z.output<typeof LiveReadyBundle>;

/** A parcel opened and not yet delivered or cancelled. */
export const LiveOpenShipment = z.object({
  shipmentId: z.string(),
  buyerUserId: z.string(),
  buyerName: z.string(),
  /** Host-only. */
  buyerEmail: z.string().nullable(),
  locationId: z.string().nullable(),
  status: z.enum(SHIPMENT_STATUSES),
  address: LiveShipmentAddress.nullable(),
  shippingCostCents: z.number().nullable(),
  carrier: z.string().nullable(),
  service: z.string().nullable(),
  labelUrl: z.string().nullable(),
  trackingNumber: z.string().nullable(),
  trackingUrl: z.string().nullable(),
  lines: z.array(LiveShippingLine),
  totalPaidCents: z.number(),
  openedAt: isoDate,
});
export type LiveOpenShipment = z.output<typeof LiveOpenShipment>;

/** Money taken for cards that do not exist. */
export const LiveRefundOwed = z.object({
  breakId: z.string(),
  breakTitle: z.string(),
  buyerUserId: z.string(),
  buyerName: z.string(),
  /** The whole charge: spots plus any postage. */
  amountCents: z.number(),
  orderIds: z.array(z.string()),
  /**
   * `cancelled_break` is expected housekeeping. `refund_failed` means an
   * automatic refund was attempted and did not go through — a payment sitting
   * somewhere it should not be.
   */
  reason: z.enum(["cancelled_break", "refund_failed"]),
});
export type LiveRefundOwed = z.output<typeof LiveRefundOwed>;

/** The Shipping tab: ready to ship (oldest wait first), open parcels (newest first), refunds owed. */
export const LiveStoreShipping = z.object({
  ready: z.array(LiveReadyBundle),
  shipments: z.array(LiveOpenShipment),
  refundsOwed: z.array(LiveRefundOwed),
  /**
   * Whether this shop can buy carrier labels. False outside the US: buyers were
   * charged no postage and the merchant posts the parcels themselves, so the
   * screen offers the address, the packing slip and "Mark posted" but no label.
   */
  carrierShipping: z.boolean(),
  /** Said once at the top when `carrierShipping` is false, so the missing button is explained. */
  shippingNote: z.string().nullable(),
});
export type LiveStoreShipping = z.output<typeof LiveStoreShipping>;

export const LiveShipmentsOpened = z.object({
  opened: z.number(),
  shipmentIds: z.array(z.string()),
});
export type LiveShipmentsOpened = z.output<typeof LiveShipmentsOpened>;

export const LiveLabel = z.object({
  carrier: z.string(),
  service: z.string(),
  labelUrl: z.string(),
  trackingNumber: z.string(),
  trackingUrl: z.string().nullable(),
  /** What the postage cost, in cents. */
  amountCents: z.number(),
});
export type LiveLabel = z.output<typeof LiveLabel>;

export const LiveShipmentAddressSaved = z.object({
  /** What the carrier said about the address. Advisory only — it is saved either way. */
  messages: z.array(z.string()),
  shippingCostCents: z.number(),
});
export type LiveShipmentAddressSaved = z.output<typeof LiveShipmentAddressSaved>;

/** One line on a packing slip. The break fields are null for a parcel that did not come from a stream. */
export const LivePackingSlipLine = z.object({
  title: z.string(),
  detail: z.string(),
  sellerNote: z.string().nullable(),
  pricePaidCents: z.number(),
  breakId: z.string().nullable(),
  breakTitle: z.string().nullable(),
  slotNumber: z.number().nullable(),
  assignedLabel: z.string().nullable(),
});
export type LivePackingSlipLine = z.output<typeof LivePackingSlipLine>;

export const LivePackingSlip = z.object({
  shipmentId: z.string(),
  /** The location it leaves from. */
  locationName: z.string().nullable(),
  buyerName: z.string(),
  address: LiveShipmentAddress.nullable(),
  lines: z.array(LivePackingSlipLine),
  totalPaidCents: z.number(),
});
export type LivePackingSlip = z.output<typeof LivePackingSlip>;

/**
 * The fulfillment export, JSON-wrapped rather than served as `text/csv`.
 *
 * The console fetches with a bearer, so it cannot navigate to a download URL —
 * it has to read the bytes and build a Blob regardless. The server names the
 * file because it is the side that knows the shop.
 */
export const LiveCsv = z.object({
  filename: z.string(),
  /** UTF-8 with a BOM, CRLF line endings. */
  csv: z.string(),
});
export type LiveCsv = z.output<typeof LiveCsv>;

// ─── Requests ────────────────────────────────────────────────────────────────

/** One box for the shop's queue: what is being opened and the label pool to draw from. No stream involved. */
export interface NewBreaks {
  title: string;
  /** S3 key of the tile image. Defaults from the sealed product. */
  imageUrl?: string;
  category: BreakCategory;
  costCents: number;
  marketValueCents: number;
  /** 150 = 1.5x, 200 = 2x. */
  targetMultiplierPct: number;
  /** The pool the randomizer draws from. Its LENGTH defines the slot count. */
  labels: string[];
  /** Positionally matched to slot numbers 1..N; null takes the recommendation. */
  perSlotPricesCents?: (number | null)[];
  /** Host-only notes, positionally matched. */
  sellerNotes?: (string | null)[];
  /**
   * The inventory product being opened. One box of it is held on its lots
   * until the break completes or is cancelled; `insufficient_stock` when the
   * shop has none on hand.
   */
  sealedProductId?: string;
  /** Not broken earlier than this. Omit for any time. */
  breakNotBefore?: Date;
}

/** What buyers see on the tile, and when it breaks. Send only what changed; `null` clears. */
export interface BreakPatch {
  title?: string;
  /** A URL from `uploads.liveCover`. */
  imageUrl?: string | null;
  /**
   * Once spots are sold: later freely, earlier only to two hours away
   * (`earlier_than_notice`), never cleared (`cleared_after_sales`).
   */
  breakNotBefore?: Date | null;
}

export interface BreakPricingPatch {
  costCents?: number;
  marketValueCents?: number;
  targetMultiplierPct?: number;
  /** Slot number to price in minor units. Sold slots are skipped. */
  perSlotPricesCents?: Record<number, number>;
}
